import { MAGIC_SOURCE_PREFIX, type SuperSourceObject } from '../super'
import { normalizeCitations } from './normalizeCitations'

// old format: {5L1T3-1,5L1T3-2}
const AskSourceReferenceRegex = new RegExp(
  `\\{(${MAGIC_SOURCE_PREFIX}[0-9]+(?:,${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\}`,
  'g'
)

// v2 format: :ref[text]{ids=5L1T3-1,5L1T3-2} or occasionally :ref[text](url){ids=5L1T3-1,5L1T3-2}
const AskSourceReferenceV2Regex = new RegExp(
  `:ref\\[([^\\]]*)\\](?:\\(([^)]+)\\))?\\{ids=(${MAGIC_SOURCE_PREFIX}[0-9]+(?:,${MAGIC_SOURCE_PREFIX}[0-9]+)*)\\}`,
  'g'
)

// LLM occasionally drops the opening `:ref[`, leaving an orphan `]{ids=...}`
// (or just `{ids=...}`) that the v2 regex can't consume. Strip the leftover
// markup so it doesn't leak into the rendered output.
const OrphanV2CitationRegex = new RegExp(
  `\\]?\\{ids=${MAGIC_SOURCE_PREFIX}[0-9]+(?:,${MAGIC_SOURCE_PREFIX}[0-9]+)*\\}`,
  'g'
)

export type CitationFormatterCallback = (params: {
  sources: SuperSourceObject[]
  citedText?: string // only with v2 format
  citedURL?: string // only with v2 format when URL is provided
  sourceIds: string[]
}) => string

export function reformatCitations({
  answer,
  sources,
  formatter,
  v2 = false, // v2-style :ref[] citations rather than simple ones
}: {
  answer: string
  sources: SuperSourceObject[]
  formatter: CitationFormatterCallback
  v2?: boolean
}) {
  if (v2) {
    answer = normalizeCitations(answer)
  }
  const regex = v2 ? AskSourceReferenceV2Regex : AskSourceReferenceRegex
  let match: RegExpExecArray | null
  let lastIndex = 0
  const sourceSet = new Set<string>()
  const finalAnswer: string[] = []

  while ((match = regex.exec(answer)) !== null) {
    const matchIndex = match.index
    const matchEnd = regex.lastIndex

    // text before the match
    if (matchIndex > lastIndex) {
      finalAnswer.push(answer.slice(lastIndex, matchIndex))
    }

    let citedText: string | undefined
    let citedURL: string | undefined
    let sourceIdsString: string | undefined

    if (v2) {
      // v2 format: :ref[text]{ids=5L1T3-1,5L1T3-2} or :ref[text](url){ids=5L1T3-1,5L1T3-2}
      citedText = match[1]
      citedURL = match[2]
      sourceIdsString = match[3]
    } else {
      // old format: {5L1T3-1,5L1T3-2}
      sourceIdsString = match[1]
    }

    const sourceIds = sourceIdsString?.split(',').map((id) => id.trim()) || []
    const matchingSources = sourceIds
      .map((id) => sources.find((s) => s.relativeId === id))
      .filter((source): source is SuperSourceObject => source !== undefined)

    sourceIds.forEach((id) => {
      if (sources.find((s) => s.relativeId === id)) {
        sourceSet.add(id)
      }
    })

    const formattedCitation = formatter({
      sources: matchingSources,
      citedText,
      citedURL,
      sourceIds,
    })

    finalAnswer.push(formattedCitation)
    lastIndex = matchEnd
  }

  // remaining text after the last match
  if (lastIndex < answer.length) {
    finalAnswer.push(answer.slice(lastIndex))
  }

  let resolved = finalAnswer.join('')
  if (v2) {
    resolved = resolved.replace(OrphanV2CitationRegex, '')
  }

  return { answer: resolved, sources: Array.from(sourceSet) }
}

/** Plain text for consumers that cannot render citations: keeps the cited words, drops the markers. */
export function stripCitations(answer: string): string {
  return reformatCitations({
    answer,
    sources: [],
    v2: true,
    formatter: ({ citedText }) => citedText ?? '',
  }).answer
}
